<script setup>
import { ref, watch, onMounted } from "vue";
import { useDirectoryStore } from "@/modules/directory/stores/directory";

const directoryStore = useDirectoryStore();

const companyQuery = ref("");
const nandinaQuery = ref("");

// Al montar el componente cargamos el listado inicial
onMounted(() => {
  directoryStore.fetchDirectory();
});

// Funciones de búsqueda
const handleSearchCompany = async () => {
  await directoryStore.searchByCompany(companyQuery.value);
};

const handleSearchNandina = async () => {
  await directoryStore.searchByNandina(nandinaQuery.value);
};

// Paginación
const handlePageChange = async (page) => {
  await directoryStore.changePage(page);
};

// Reactividad: si se limpia el texto, se recarga el listado completo
watch(companyQuery, (newVal) => {
  if (newVal.trim() === "") {
    directoryStore.resetFilters();
    directoryStore.fetchDirectory();
  }
});

watch(nandinaQuery, (newVal) => {
  if (newVal.trim() === "") {
    directoryStore.resetFilters();
    directoryStore.fetchDirectory();
  }
});
</script>

<template>
  <div class="container py-5">
    <div class="row g-5">
      <!-- Panel lateral (buscador) -->
      <div class="col-xl-4 order-1 order-xl-2">
        <div class="card card-body border p-4 shadow-lg rounded-3">
          <h5 class="mb-4 text-success">Buscar</h5>

          <!-- Buscar empresa -->
          <form @submit.prevent="handleSearchCompany" class="mb-4">
            <input
              v-model="companyQuery"
              class="form-control pe-5 bg-transparent"
              type="search"
              placeholder="Empresa"
            />
            <button
              type="submit"
              class="btn btn-green mt-3 w-100 fw-semibold"
              :disabled="directoryStore.loading"
            >
              <span v-if="directoryStore.loading">
                <span class="spinner-border spinner-border-sm me-2"></span>
                Buscando...
              </span>
              <span v-else>Buscar empresa</span>
            </button>
          </form>

          <!-- Buscar producto -->
          <form @submit.prevent="handleSearchNandina">
            <input
              v-model="nandinaQuery"
              class="form-control pe-5 bg-transparent"
              type="search"
              placeholder="Producto o NANDINA"
            />
            <button
              type="submit"
              class="btn btn-green mt-3 w-100 fw-semibold"
              :disabled="directoryStore.loading"
            >
              <span v-if="directoryStore.loading">
                <span class="spinner-border spinner-border-sm me-2"></span>
                Buscando...
              </span>
              <span v-else>Buscar producto</span>
            </button>
          </form>
        </div>
      </div>

      <!-- Listado principal -->
      <div class="col-xl-8 order-2 order-xl-1">
        <h2 class="mb-4 text-success">Directorio</h2>

        <!-- Estado de carga -->
        <div v-if="directoryStore.loading" class="py-5 text-center">
          <div class="spinner-border text-success" role="status"></div>
        </div>

        <!-- Sin resultados -->
        <div
          v-else-if="directoryStore.companies.length === 0"
          class="py-5 text-center"
        >
          <p>No se encontraron resultados.</p>
        </div>

        <!-- Resultados -->
        <div v-else class="card-body p-0 pt-3">
          <div
            v-for="company in directoryStore.companies"
            :key="company.id"
            class="border-bottom py-3"
          >
            <router-link
              :to="{ name: 'directory-detail', params: { id: company.id } }"
              class="text-decoration-none text-dark hover-green"
            >
              <h5 class="fw-semibold mb-1 d-flex align-items-center">
                <img
                  src="assets/images/flecha-icon.png"
                  width="20"
                  class="me-2"
                />
                {{ company.name }}
              </h5>
            </router-link>
          </div>

          <!-- Paginación -->
          <nav
            v-if="directoryStore.totalPages > 1"
            class="mt-5 d-flex justify-content-center"
          >
            <ul class="pagination">
              <li
                class="page-item"
                :class="{ disabled: directoryStore.currentPage === 1 }"
              >
                <button
                  class="page-link"
                  @click="handlePageChange(directoryStore.currentPage - 1)"
                >
                  Anterior
                </button>
              </li>

              <li
                v-for="page in directoryStore.totalPages"
                :key="page"
                class="page-item"
                :class="{ active: directoryStore.currentPage === page }"
              >
                <button class="page-link" @click="handlePageChange(page)">
                  {{ page }}
                </button>
              </li>

              <li
                class="page-item"
                :class="{
                  disabled:
                    directoryStore.currentPage === directoryStore.totalPages,
                }"
              >
                <button
                  class="page-link"
                  @click="handlePageChange(directoryStore.currentPage + 1)"
                >
                  Siguiente
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.btn-green {
  background-color: #3ca94d;
  border-color: #3ca94d;
  color: white;
  transition: all 0.2s ease-in-out;
}
.btn-green:hover {
  background-color: #319040;
  border-color: #319040;
}

.hover-green:hover {
  color: #3ca94d !important;
}

.pagination .active .page-link {
  background-color: #3ca94d;
  border-color: #3ca94d;
}

.container {
  padding-top: 2rem;
  padding-bottom: 2rem;
}
h2{
  font-family: "Merriweather";
}
h5 {
  line-height: 1.4;
  font-family: "Merriweather";
}

@media (max-width: 991px) {
  .card {
    margin-bottom: 1.5rem;
  }
}
</style>
